import {
	ConfidenceLevel,
	DataRequestStatus,
	EmissionValueGranularityLevel,
} from '@api-client'
import { Datepicker } from '@components'
import {
	Box,
	Button,
	ClickAwayListener,
	FormControlLabel,
	Slider,
} from '@mui/material'
import { uniq } from 'lodash-es'
import { useSnackbar } from 'notistack'
import { type ComponentProps, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { IAppliedFilters } from '../types'
import { CheckboxWithValue } from './CheckboxWithValue'
import { CustomerListFilter } from './CustomerListFilter'
import {
	ActionButtons,
	CloseIconWrapper,
	CustomTextField,
	Divider,
	DrawerFiltersWrapper,
	FormGroupWrapper,
	Header,
	SectionContent,
	SectionHeader,
} from './styles'
import { SupplierListFilter } from './SupplierListFilter'

interface IProps {
	appliedFilters: IAppliedFilters
	isAlreadyFilteringBySupplier?: boolean
	isCustomer: boolean
	onApplyFilters: (filters: IAppliedFilters) => void
	onClose: () => void
}

const getShareOfPrimaryDataValueText = (value: number) => `${value}%`
const SHARE_OF_PRIMARY_DATA_MARKS: ComponentProps<typeof Slider>['marks'] = [
	{ value: 0, label: '0%' },
	{ value: 100, label: '100%' },
]

const DrawerFilters: React.FC<IProps> = ({
	appliedFilters,
	isAlreadyFilteringBySupplier,
	isCustomer,
	onApplyFilters,
	onClose,
}: IProps) => {
	const { t } = useTranslation()
	const { enqueueSnackbar } = useSnackbar()
	const [shareOfPrimaryData, setShareOfPrimaryData] = useState<number[]>([
		appliedFilters.shareOfPrimaryDataInEmissionFactor?.from || 0,
		appliedFilters.shareOfPrimaryDataInEmissionFactor?.to || 100,
	])
	const [emissionValue, setEmissionValue] = useState<
		readonly (number | undefined)[]
	>([
		appliedFilters.emissionValue?.from || undefined,
		appliedFilters.emissionValue?.to || undefined,
	])
	const [verified, setVerified] = useState<readonly boolean[]>(
		appliedFilters.emissionDataVerified || []
	)
	const [productRequestStatus, setProductRequestStatus] = useState<
		readonly DataRequestStatus[]
	>(appliedFilters.productRequestStatus || [])
	const [granularity, setGranularity] = useState<
		readonly EmissionValueGranularityLevel[]
	>(appliedFilters.emissionValueGranularity || [])
	const [confidence, setConfidence] = useState<readonly ConfidenceLevel[]>(
		appliedFilters.emissionValueConfidence || []
	)
	const [lastUpdatedDate, setLastUpdatedDate] = useState<readonly string[]>([
		appliedFilters.lastUpdated?.from || '',
		appliedFilters.lastUpdated?.to || '',
	])
	const [selectedSuppliers, setSelectedSuppliers] = useState<readonly string[]>(
		appliedFilters.selectedSuppliers || []
	)
	const [selectedCustomers, setSelectedCustomers] = useState<readonly string[]>(
		appliedFilters.selectedCustomers || []
	)

	const handleChangeShareOfPrimaryData = useCallback(
		(event: Event, newValue: number | number[]) =>
			setShareOfPrimaryData(newValue as number[]),
		[]
	)

	const resetFilters = useCallback(() => {
		onApplyFilters({})
		onClose()
	}, [onApplyFilters, onClose])

	const applyFilters = useCallback(() => {
		const filters = {} as IAppliedFilters
		if (productRequestStatus.length > 0) {
			filters.productRequestStatus = productRequestStatus
		}
		if (granularity.length > 0) {
			filters.emissionValueGranularity = granularity
		}
		if (verified.length > 0) {
			filters.emissionDataVerified = verified
		}
		if (confidence.length > 0) {
			filters.emissionValueConfidence = confidence
		}

		const [primaryFrom, primaryTo] = shareOfPrimaryData
		if (primaryFrom !== 0 || primaryTo !== 100) {
			filters.shareOfPrimaryDataInEmissionFactor = {}
			if (primaryFrom !== 0) {
				filters.shareOfPrimaryDataInEmissionFactor.from = primaryFrom
			}
			if (primaryTo !== 100) {
				filters.shareOfPrimaryDataInEmissionFactor.to = primaryTo
			}
		}

		const [emissionFrom, emissionTo] = emissionValue

		if (emissionFrom || emissionTo) {
			filters.emissionValue = {}
			if (emissionFrom) {
				filters.emissionValue.from = emissionFrom
			}
			if (emissionTo) {
				filters.emissionValue.to = emissionTo
			}
		}

		const [lastUpdatedFrom, lastUpdatedTo] = lastUpdatedDate
		if (lastUpdatedFrom || lastUpdatedTo) {
			filters.lastUpdated = {}
			if (lastUpdatedFrom) {
				filters.lastUpdated.from = lastUpdatedFrom
			}
			if (lastUpdatedTo) {
				filters.lastUpdated.to = lastUpdatedTo
			}
		}

		if (selectedSuppliers.length > 0) {
			filters.selectedSuppliers = selectedSuppliers
		}

		if (selectedCustomers.length > 0) {
			filters.selectedCustomers = selectedCustomers
		}

		onApplyFilters(filters)
		onClose()
	}, [
		productRequestStatus,
		granularity,
		verified,
		confidence,
		shareOfPrimaryData,
		emissionValue,
		lastUpdatedDate,
		selectedSuppliers,
		selectedCustomers,
		onApplyFilters,
		onClose,
	])

	const handleProductRequestStatusChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>, value: DataRequestStatus) => {
			const { checked } = event.target
			let newProductRequestStatus
			if (checked) {
				newProductRequestStatus = uniq([...productRequestStatus, value])
			} else {
				newProductRequestStatus = productRequestStatus.filter(
					status => status !== value
				)
			}

			setProductRequestStatus(newProductRequestStatus)
		},
		[productRequestStatus]
	)

	const handleGranularityChange = useCallback(
		(
			event: React.ChangeEvent<HTMLInputElement>,
			value: EmissionValueGranularityLevel
		) => {
			const { checked } = event.target
			let newGranularity
			if (checked) {
				newGranularity = uniq([...granularity, value])
			} else {
				newGranularity = granularity.filter(type => type !== value)
			}

			setGranularity(newGranularity)
		},
		[granularity]
	)

	const handleVerifiedChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
			const { checked } = event.target
			const newVerified = checked
				? uniq([...verified, value])
				: verified.filter(val => val !== value)

			setVerified(newVerified)
		},
		[verified]
	)

	const handleConfidenceChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>, value: ConfidenceLevel) => {
			const { checked } = event.target
			let newConfidence
			if (checked) {
				newConfidence = uniq([...confidence, value])
			} else {
				newConfidence = confidence.filter(type => type !== value)
			}

			setConfidence(newConfidence)
		},
		[confidence]
	)

	const handleEmissionValueChange = useCallback(
		(
			event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
			index: number
		) => {
			const emissionValueCopy = [...emissionValue]
			emissionValueCopy[index] = +event.target.value
			setEmissionValue(emissionValueCopy)
		},
		[emissionValue]
	)

	const handleLastUpdatedDateChange = useCallback(
		(date: Date | null, id: 'from' | 'to') => {
			const idMapping = {
				from: 0,
				to: 1,
			}
			const year = date?.getFullYear()
			const month = date?.getMonth()

			if (!year || !month) {
				return
			}

			let newDate
			if (id === 'from') {
				newDate = new Date(year, month, 1)
			} else if (id === 'to') {
				newDate = new Date(year, month + 1, 0)
			}
			const currentDateFrom = lastUpdatedDate[0]
			const currentDateTo = lastUpdatedDate[1]

			// disable specific dates
			if (newDate && id === 'from' && currentDateTo) {
				const to = new Date(currentDateTo)
				if (newDate?.getTime() > to.getTime()) {
					enqueueSnackbar(t('toast.error.filtersDateFrom'), {
						variant: 'warning',
					})
					return
				}
			}

			// disable specific dates
			if (newDate && id === 'to' && currentDateFrom) {
				const from = new Date(currentDateFrom)
				if (newDate?.getTime() < from.getTime()) {
					enqueueSnackbar(t('toast.error.filtersDateTo'), {
						variant: 'warning',
					})
					return
				}
			}
			const lastUpdatedDateCopy = [...lastUpdatedDate]
			lastUpdatedDateCopy[idMapping[id]] = newDate?.toISOString() || ''
			setLastUpdatedDate(lastUpdatedDateCopy)
		},
		[lastUpdatedDate, enqueueSnackbar, t]
	)

	const onCustomerListChange = useCallback(
		(checked: boolean, value: string) => {
			const newSelectedCustomers = [...selectedCustomers]
			if (checked) {
				newSelectedCustomers.push(value)
			} else {
				const index = newSelectedCustomers.indexOf(value)
				newSelectedCustomers.splice(index, 1)
			}
			setSelectedCustomers(newSelectedCustomers)
		},
		[selectedCustomers]
	)

	const handleLastUpdatedDateChangeFrom = useCallback(
		(date: Date | null) => handleLastUpdatedDateChange(date, 'from'),
		[handleLastUpdatedDateChange]
	)

	const handleLastUpdatedDateChangeTo = useCallback(
		(date: Date | null) => handleLastUpdatedDateChange(date, 'to'),
		[handleLastUpdatedDateChange]
	)

	const handleEmissionValueChangeFrom = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
			handleEmissionValueChange(e, 0),
		[handleEmissionValueChange]
	)

	const handleEmissionValueChangeTo = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
			handleEmissionValueChange(e, 1),
		[handleEmissionValueChange]
	)

	const getDrawerLabel = useCallback(() => t('filters.shareOfPrimaryData'), [t])

	const getStatusLabel = (context: DataRequestStatus) =>
		isCustomer
			? t('request.asCustomer.status', { context })
			: t('request.asSupplier.status', { context })

	return (
		<ClickAwayListener onClickAway={onClose}>
			<DrawerFiltersWrapper
				display="flex"
				justifyContent="space-between"
				alignItems="flex-start"
				flexDirection="column"
			>
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					width="100%"
					marginBottom="20px"
				>
					<Header component="div">{t('filters.header')}</Header>
					<CloseIconWrapper onClick={onClose} />
				</Box>

				<Divider />
				<FormGroupWrapper>
					<SectionHeader component="div">{t('table.status')}</SectionHeader>
					<SectionContent>
						{[
							DataRequestStatus.NOT_STARTED,
							DataRequestStatus.REQUESTED,
							DataRequestStatus.REQUESTED_CLARIFICATION,
							DataRequestStatus.REJECTED,
							DataRequestStatus.DECLINED,
							DataRequestStatus.PENDING_REVIEW,
							DataRequestStatus.COMPLETED,
						].map(status => (
							<FormControlLabel
								key={status}
								control={
									<CheckboxWithValue
										checked={productRequestStatus.includes(status)}
										value={status}
										onChange={handleProductRequestStatusChange}
									/>
								}
								label={getStatusLabel(status)}
							/>
						))}
					</SectionContent>
				</FormGroupWrapper>
				<Divider />
				<FormGroupWrapper>
					<SectionHeader component="div">
						{t('formField.verification')}
					</SectionHeader>
					<SectionContent>
						<FormControlLabel
							control={
								<CheckboxWithValue
									value
									checked={verified.includes(true)}
									onChange={handleVerifiedChange}
								/>
							}
							label={t('emissions.verification.name', {
								context: 'true',
							})}
						/>
						<FormControlLabel
							control={
								<CheckboxWithValue
									value={false}
									checked={verified.includes(false)}
									onChange={handleVerifiedChange}
								/>
							}
							label={t('emissions.verification.name', {
								context: 'false',
							})}
						/>
					</SectionContent>
				</FormGroupWrapper>
				<Divider />
				<FormGroupWrapper>
					<SectionHeader component="div">
						{t('filters.lastUpdate.header')}
					</SectionHeader>
					<SectionContent>
						<Box display="flex" alignItems="center" gap="5px">
							<Datepicker
								selectedValue={lastUpdatedDate[0]}
								onChangeHandler={handleLastUpdatedDateChangeFrom}
							/>
							{t('filters.lastUpdate.to')}
							<Datepicker
								selectedValue={lastUpdatedDate[1]}
								onChangeHandler={handleLastUpdatedDateChangeTo}
							/>
						</Box>
					</SectionContent>
				</FormGroupWrapper>
				<Divider />
				<FormGroupWrapper>
					<SectionHeader component="div">
						{t('filters.emissionValue.header')}
					</SectionHeader>
					<SectionContent>
						<Box display="flex" alignItems="center" gap="5px">
							<CustomTextField
								variant="outlined"
								type="number"
								value={emissionValue[0] === undefined ? '' : emissionValue[0]}
								onChange={handleEmissionValueChangeFrom}
							/>
							{t('filters.emissionValue.to')}
							<CustomTextField
								variant="outlined"
								type="number"
								value={emissionValue[1] === undefined ? '' : emissionValue[1]}
								onChange={handleEmissionValueChangeTo}
							/>
							{t('filters.emissionValue.unit')}
						</Box>
					</SectionContent>
				</FormGroupWrapper>
				<Divider />
				<FormGroupWrapper>
					<SectionHeader component="div">
						{t('table.granularity')}
					</SectionHeader>
					<SectionContent>
						<FormControlLabel
							control={
								<CheckboxWithValue
									checked={granularity.includes(
										EmissionValueGranularityLevel.DETAILED_LIFECYCLE
									)}
									value={EmissionValueGranularityLevel.DETAILED_LIFECYCLE}
									onChange={handleGranularityChange}
								/>
							}
							label={t('emissions.granularity.name', {
								context: EmissionValueGranularityLevel.DETAILED_LIFECYCLE,
							})}
						/>
						<FormControlLabel
							control={
								<CheckboxWithValue
									checked={granularity.includes(
										EmissionValueGranularityLevel.LIFECYCLE
									)}
									value={EmissionValueGranularityLevel.LIFECYCLE}
									onChange={handleGranularityChange}
								/>
							}
							label={t('emissions.granularity.name', {
								context: EmissionValueGranularityLevel.LIFECYCLE,
							})}
						/>
						<FormControlLabel
							control={
								<CheckboxWithValue
									checked={granularity.includes(
										EmissionValueGranularityLevel.PRODUCT
									)}
									value={EmissionValueGranularityLevel.PRODUCT}
									onChange={handleGranularityChange}
								/>
							}
							label={t('emissions.granularity.name', {
								context: EmissionValueGranularityLevel.PRODUCT,
							})}
						/>
						<FormControlLabel
							control={
								<CheckboxWithValue
									checked={granularity.includes(
										EmissionValueGranularityLevel.CALCULATOR
									)}
									value={EmissionValueGranularityLevel.CALCULATOR}
									onChange={handleGranularityChange}
								/>
							}
							label={t('emissions.granularity.name', {
								context: EmissionValueGranularityLevel.CALCULATOR,
							})}
						/>
						<FormControlLabel
							control={
								<CheckboxWithValue
									checked={granularity.includes(
										EmissionValueGranularityLevel.SHARE_COMPANY
									)}
									value={EmissionValueGranularityLevel.SHARE_COMPANY}
									onChange={handleGranularityChange}
								/>
							}
							label={t('emissions.granularity.name', {
								context: EmissionValueGranularityLevel.SHARE_COMPANY,
							})}
						/>
						<FormControlLabel
							control={
								<CheckboxWithValue
									checked={granularity.includes(
										EmissionValueGranularityLevel.UNAVAILABLE
									)}
									value={EmissionValueGranularityLevel.UNAVAILABLE}
									onChange={handleGranularityChange}
								/>
							}
							label={t('emissions.granularity.name', {
								context: EmissionValueGranularityLevel.UNAVAILABLE,
							})}
						/>
					</SectionContent>
				</FormGroupWrapper>

				<Divider />
				<FormGroupWrapper>
					<SectionHeader component="div">
						{t('table.confidenceLevel')}
					</SectionHeader>
					<SectionContent>
						<FormControlLabel
							control={
								<CheckboxWithValue
									checked={confidence.includes(ConfidenceLevel.NONE)}
									value={ConfidenceLevel.NONE}
									onChange={handleConfidenceChange}
								/>
							}
							label={t('emissions.confidence.name', {
								context: ConfidenceLevel.NONE,
							})}
						/>
						<FormControlLabel
							control={
								<CheckboxWithValue
									checked={confidence.includes(ConfidenceLevel.SOMEWHAT)}
									value={ConfidenceLevel.SOMEWHAT}
									onChange={handleConfidenceChange}
								/>
							}
							label={t('emissions.confidence.name', {
								context: ConfidenceLevel.SOMEWHAT,
							})}
						/>
						<FormControlLabel
							control={
								<CheckboxWithValue
									checked={confidence.includes(ConfidenceLevel.VERY)}
									value={ConfidenceLevel.VERY}
									onChange={handleConfidenceChange}
								/>
							}
							label={t('emissions.confidence.name', {
								context: ConfidenceLevel.VERY,
							})}
						/>
					</SectionContent>
				</FormGroupWrapper>
				<Divider />
				<FormGroupWrapper>
					<SectionHeader component="div">
						{t('filters.shareOfPrimaryData')}
					</SectionHeader>
					<SectionContent margin="40px 10px 0 10px">
						<Slider
							getAriaLabel={getDrawerLabel}
							// TODO(pascal): Fix MUI to accept a readonly array.
							value={shareOfPrimaryData}
							onChange={handleChangeShareOfPrimaryData}
							valueLabelDisplay="on"
							getAriaValueText={getShareOfPrimaryDataValueText}
							marks={SHARE_OF_PRIMARY_DATA_MARKS}
						/>
					</SectionContent>
				</FormGroupWrapper>
				<Divider />
				{isCustomer && !isAlreadyFilteringBySupplier && (
					<SupplierListFilter
						selectedSuppliers={selectedSuppliers}
						onSuppliersListChange={setSelectedSuppliers}
					/>
				)}
				{!isCustomer && (
					<CustomerListFilter
						selectedCustomers={selectedCustomers}
						onCustomerListChange={onCustomerListChange}
					/>
				)}
				<ActionButtons>
					<Divider />
					<Box display="flex" justifyContent="flex-end" gap="15px">
						<Button variant="outlined" color="primary" onClick={resetFilters}>
							{t('button.resetFilters')}
						</Button>
						<Button variant="contained" color="primary" onClick={applyFilters}>
							{t('button.applyFilters')}
						</Button>
					</Box>
				</ActionButtons>
			</DrawerFiltersWrapper>
		</ClickAwayListener>
	)
}

export { DrawerFilters }
